import { useTranslation } from 'react-i18next'
import { FunctionComponent, SVGProps, useMemo, useCallback, useState } from 'react'
import {
  pipe,
  propEq,
  prop,
  map,
  evolve,
  filter,
  reject,
  isEmpty,
  flatten,
  anyPass,
  path
} from 'ramda'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  Text,
  Table,
  TableColumnDefinition,
  Stack,
  ResponsiveRender,
  useDialogControllers
} from '@machobear/design-system'
import useGetPlanKey from '@/hooks/use-get-plan-key'
import config from '@/config'
import {
  TenantScreening,
  RentalAdvertising,
  LeaseaAndPayments,
  UsersIcon,
  InfoCircleDarkBlue
} from '@/components/icons'
import useMyListings from '@/pages/Listing/MyListings/hooks/use-my-listings'
import { useGetUsages } from '@/hooks/use-block-and-pay'
import useHasCustomCompanyPage from '@/hooks/use-has-custom-company-page'
import toBoolean from '@/utils/to-boolean'
import { PlanType } from '../../../types'
import DialogWrapper from '../../Dialogs/dialog-wrapper'
import MobileCard from '../moblile-card'
import { productStyle } from '../index.css'
import { getListingLabel } from '../../../utils/get-listing-label'

const { WEB_URL, APP_PATH } = config

type Block = {
  name: string
  value: number
  usage: {
    current_value: number
  }
  current_value: number
}
type modalOptions =
  | 'addendumsDialog'
  | 'tenantScreeningOptionsDialog'
  | 'tenantScreeningDialog'
  | 'warningDialog'
  | 'syndicationDialog'
type Props = {
  currentPlan: PlanType
}
type Blocks = {
  company_id: string
  blocks?: {
    credit_checks: Block
    managed_users: Block
    syndication: Block
  }
}

type TableItem = {
  title: string
  icon: FunctionComponent<SVGProps<SVGSVGElement>>
  credits: string
  useCreditVariant: 'solid' | 'ghost' | 'transparent' | 'soft' | undefined
  useCreditText: string
  useCreditId: string
  useCreditDisabled?: boolean
  useCreditOnClick: () => void
  buyMoreOnClick?: () => void
  buyMoreId?: string
}
const filterActiveListings = pipe(
  map(
    evolve({
      listings: filter(
        anyPass([propEq('state_machine', 'published'), propEq('state_machine', 'pending')])
      )
    })
  ),
  reject((building: any) => isEmpty(building.listings)),
  map(prop('listings')),
  flatten
)

const PlanProducts = ({ currentPlan }: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { data }: { data: Blocks; isLoading: boolean } = useGetUsages()
  const { listings: allMyListings } = useMyListings()
  const [selectedListing, setSelectedListing] = useState<{
    listingId: string
    address: string
  }>({ listingId: '', address: '' })
  const activeListings = filterActiveListings(allMyListings || [])
  const noListings = isEmpty(activeListings)
  const companyId = data?.company_id
  const hasCustomCompanyPage = useHasCustomCompanyPage(companyId)
  const isGrowthPlan =
    currentPlan === 'growth-plan-monthly' ||
    currentPlan === 'growth-plan-annual' ||
    currentPlan === 'growth-plan-monthly-coupon-wbrothers'

  const planText = useGetPlanKey()
  const creditChecks = data?.blocks?.credit_checks

  const syndicationCredits = data?.blocks?.syndication
  const managedUsers = data?.blocks?.managed_users
  const { visible, showModal, closeModal } = useDialogControllers({
    addendumsDialog: false,
    tenantScreeningOptionsDialog: false,
    tenantScreeningDialog: false,
    syndicationDialog: false,
    warningDialog: false,
    buySyndicationCredits: false,
    buyTenantScreening: false,
    useSyndicationCredits: false,
    advertiseYourListing: false,
    analyticsReport: false,
    checkoutDialog: false,
    editPaymentDialog: false,
    sponsoredListingDialog: false,
    buySponsoredListing: false,
    billingUser: false
  })

  const skipSyndicationModal = toBoolean(
    useSelector(path(['session', 'user_flags', 'skip_syndication_info_modal', 'key_value']))
  )

  const handleSyndicationSubmit = (listingData: {
    listing_id: string
    unit_number: string
    address: string
  }) => {
    setSelectedListing({ listingId: listingData.listing_id, address: getListingLabel(listingData) })
    showModal('useSyndicationCredits')()
  }
  const handleDialogOpen = useCallback(
    (id: modalOptions) => (noListings ? showModal('warningDialog') : showModal(id)),
    [noListings, showModal]
  )

  const handleUseSyndication = useCallback(() => {
    if (skipSyndicationModal) {
      handleDialogOpen('syndicationDialog')()
    } else {
      showModal('advertiseYourListing')()
    }
  }, [skipSyndicationModal, showModal, handleDialogOpen])

  const openCompanyPage = useCallback(() => {
    window.open(`${WEB_URL}/company/${data?.company_id}`, '_blank')
  }, [data?.company_id])

  const handleSendMessage = useCallback(
    async (message: string) => {
      await dispatch.supportRoom.sendMessage({ message })
      window.open(`${APP_PATH}/chat/support-room`, '_blank')
    },
    [dispatch.supportRoom]
  )

  const handleSendAnalytics = useCallback(() => {
    handleSendMessage(
      `Hi there,
      I am interested in unlocking a monthly analytics report.
      If you could please provide more information and next steps, I would greatly appreciate it.`
    )
  }, [handleSendMessage])
  const handleSendCompanyPages = useCallback(() => {
    handleSendMessage(
      `Hi there,
      I am interested in unlocking a branded company landing page.
      If you could please provide more information and next steps, I would greatly appreciate it.`
    )
  }, [handleSendMessage])

  const columnDefinition: TableColumnDefinition = useMemo(
    () => [
      {
        accessor: 'title',
        Header: t('l.credit_usage.product.product.title', 'Product'),
        gridWidth: 'fill-available',
        disableSortBy: true,
        Cell: (cell: { value: string; row: { original: any } }) => {
          const Icon = cell?.row?.original?.icon
          const showInfoIcon = cell?.row?.original?.showInfoIcon
          return (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="flexStart"
              className={productStyle}
            >
              <Box paddingRight="xsmall">
                <Icon />
              </Box>
              <Text size="small">{cell?.value}</Text>
              {showInfoIcon && (
                <Box
                  onClick={cell?.row?.original?.infoAction}
                  paddingLeft="xxsmall"
                  cursor="pointer"
                >
                  <InfoCircleDarkBlue />
                </Box>
              )}
            </Box>
          )
        }
      },
      {
        accessor: 'credits',
        Header: t('l.credit_usage.product.credits.title', 'Credits'),
        gridWidth: 'fill-available',
        disableSortBy: true,
        Cell: ({ value }: { value?: string }) => {
          return value ? <Text size="small">{value}</Text> : null
        }
      },
      {
        accessor: 'manage',
        gridWidth: 'fill-available',
        disableSortBy: true,
        Cell: (cell: { value: string; row: { original: any } }) => {
          const value = cell?.row?.original
          return (
            value?.buyMoreOnClick && (
              <Box display="flex" flexDirection="column" alignItems={'flexEnd'} width="full">
                <Box style={{ width: '126px' }}>
                  <Button
                    size="small"
                    variant="solid"
                    onClick={value?.buyMoreOnClick}
                    id={value?.buyMoreId}
                  >
                    <Box width="full">
                      {t('l.credit_usage.product.buy_more.action', 'Buy more')}
                    </Box>
                  </Button>
                </Box>
              </Box>
            )
          )
        }
      },
      {
        accessor: 'button',
        gridWidth: 'fill-available',
        disableSortBy: true,
        Cell: (cell: { value: string; row: { original: any } }) => {
          const value = cell?.row?.original
          return (
            <Box display="flex" flexDirection="column" alignItems={'flexEnd'} width="full">
              <Box style={{ width: '127px' }}>
                <Button
                  size="small"
                  variant={value?.useCreditVariant}
                  onClick={value?.useCreditOnClick}
                  disabled={value?.useCreditDisabled}
                  id={value?.useCreditId}
                  data={{ testId: value?.useCreditId }}
                >
                  <Box width="full">{value?.useCreditText}</Box>
                </Button>
              </Box>
            </Box>
          )
        }
      }
    ],
    [t]
  )

  const getAdditionalUsersDetails = useCallback(() => {
    if (managedUsers?.usage?.current_value !== managedUsers?.value) {
      return {
        useCreditText: t('l.credit_usage.product.upgrade_plan.title', 'Add users'),
        useCreditOnClick: () => window.open(`${APP_PATH}/manage/users/create`, '_blank')
      }
    }

    return isGrowthPlan
      ? {
          useCreditText: t('l.credit_usage.product.upgrade_plan.title', 'Upgrade Plan'),
          useCreditOnClick: () => {
            window.open(`${WEB_URL}/pricing`, '_blank')
          }
        }
      : {
          useCreditText: t('l.credit_usage.product.contact_sales.title', 'Contact sales'),
          useCreditOnClick: () => {}
        }
  }, [t, isGrowthPlan, managedUsers?.usage?.current_value, managedUsers?.value])

  const TENANT_SCREENING: TableItem = useMemo(
    () => ({
      useCreditId: `usage-credit-${planText}-tenant-screen-rpt-use-credit-btn-click`,
      title: t('l.credit_usage.product.tenant_screening.title', 'Tenant Screening Report'),
      icon: TenantScreening,
      useCreditText: t('l.credit_usage.product.use_credit.action', 'Use credit'),
      useCreditVariant: 'ghost',
      useCreditOnClick: handleDialogOpen('tenantScreeningOptionsDialog'),
      useCreditDisabled: Boolean(
        creditChecks?.value && creditChecks?.value - creditChecks?.usage?.current_value === 0
      ),
      buyMoreOnClick: showModal('buyTenantScreening'),
      buyMoreId: `usage-credit-${planText}-alacarte-tenant-screen-rpt-buy-more-btn-click`,
      credits: creditChecks?.value
        ? `${creditChecks?.current_value - creditChecks?.usage?.current_value} / ${
            creditChecks?.value
          }`
        : ''
    }),
    [
      planText,
      t,
      creditChecks?.value,
      creditChecks?.usage?.current_value,
      creditChecks?.current_value,
      handleDialogOpen,
      showModal
    ]
  )

  const SYNDICATION: TableItem = useMemo(
    () => ({
      useCreditId: `usage-credit-${planText}-multiplatform-adv-use-credit-btn-click`,
      title: t('l.credit_usage.product.syndication.title', 'Multi-Platform Advertising'),
      icon: RentalAdvertising,
      useCreditText: t('l.credit_usage.product.use_credit.action', 'Use Credit'),
      useCreditVariant: 'ghost',
      useCreditOnClick: handleUseSyndication,
      credits: syndicationCredits?.value
        ? `${syndicationCredits?.current_value - syndicationCredits?.usage?.current_value} / ${
            syndicationCredits?.value
          }`
        : '',
      useCreditDisabled: Boolean(
        syndicationCredits?.value &&
          syndicationCredits?.value - syndicationCredits?.usage?.current_value === 0
      ),
      buyMoreOnClick: showModal('buySyndicationCredits'),
      buyMoreId: `usage-credit-${planText}-alacarte-multiplatform-adv-buy-more-btn-click`,
      showInfoIcon: true,
      infoAction: showModal('advertiseYourListing')
    }),
    [
      t,
      planText,
      showModal,
      handleUseSyndication,
      syndicationCredits?.value,
      syndicationCredits?.usage?.current_value,
      syndicationCredits?.current_value
    ]
  )

  const GROWTH_COMPANY_PAGE: TableItem[] = useMemo(
    () => [
      {
        useCreditId: `usage-credit-${planText}-co-landing-page-standard-view-page-btn-click`,
        title: t(
          'l.credit_usage.product.company_page_standard.title',
          'Company Landing Page - Standard'
        ),

        icon: RentalAdvertising,
        useCreditText: t('l.credit_usage.product.view_page.action', 'View page'),
        useCreditVariant: 'ghost',
        credits: t('l.credit_usage.product.included.title', 'Included'),
        useCreditOnClick: openCompanyPage
      }
    ],
    [t, openCompanyPage, planText]
  )

  const BUSINESS_COMPANY_PAGE: TableItem[] = useMemo(() => {
    return hasCustomCompanyPage
      ? [
          {
            useCreditId: hasCustomCompanyPage
              ? `usage-credit-${planText}-co-landing-page-custom-view-page-btn-click`
              : `usage-credit-${planText}-co-landing-page-custom-contact-sales-btn-click`,
            title: t(
              'l.credit_usage.product.company_page_custom.title',
              'Company Landing Page - Custom'
            ),
            icon: RentalAdvertising,
            useCreditText: hasCustomCompanyPage
              ? t('l.credit_usage.product.view_page.action', 'View page')
              : t('l.credit_usage.product.contact_sales.action', 'Contact sales'),
            useCreditOnClick: hasCustomCompanyPage ? openCompanyPage : handleSendCompanyPages,
            useCreditVariant: 'ghost',
            credits: t('l.credit_usage.product.included.title', 'Included')
          }
        ]
      : [
          ...GROWTH_COMPANY_PAGE,
          {
            useCreditId: hasCustomCompanyPage
              ? `usage-credit-${planText}-co-landing-page-custom-view-page-btn-click`
              : `usage-credit-${planText}-co-landing-page-custom-contact-sales-btn-click`,
            title: t(
              'l.credit_usage.product.company_page_custom.title',
              'Company Landing Page - Custom'
            ),
            icon: RentalAdvertising,
            useCreditText: t('l.credit_usage.product.contact_sales.action', 'Contact sales'),
            useCreditOnClick: handleSendCompanyPages,
            useCreditVariant: 'ghost',
            credits: t('l.credit_usage.product.included.title', 'Included')
          }
        ]
  }, [
    t,
    hasCustomCompanyPage,
    GROWTH_COMPANY_PAGE,
    handleSendCompanyPages,
    planText,
    openCompanyPage
  ])

  const ANALYTICS: TableItem[] = useMemo(
    () => [
      {
        title: t('l.credit_usage.product.analytics.title', 'Monthly Analytics Report'),
        useCreditId: `usage-credit-${planText}-monthly-analytics-rpt-contact-sales-btn-click`,
        icon: RentalAdvertising,
        useCreditText: t('l.credit_usage.product.contact_sales.action', 'Contact Sales'),
        useCreditVariant: 'ghost',
        credits: t('l.credit_usage.product.addon.title', 'Add-on'),
        useCreditOnClick: handleSendAnalytics,
        showInfoIcon: true,
        infoAction: showModal('analyticsReport')
      }
    ],
    [t, handleSendAnalytics, planText, showModal]
  )

  const STANDARD_ADDENDUMS: TableItem[] = useMemo(
    () => [
      {
        useCreditId: `usage-credit-${planText}-livrent-addendums-use-credit-btn-click`,
        title: t('l.credit_usage.product.addendums.title', 'Addendums by liv.rent'),
        icon: LeaseaAndPayments,
        useCreditText: t('l.credit_usage.product.use_credit.action', 'Use Credit'),
        useCreditVariant: 'ghost',
        useCreditOnClick: showModal('addendumsDialog'),
        credits: t('l.credit_usage.product.unlimited.title', 'Unlimited')
      },
      {
        useCreditId: `usage-credit-${planText}-onetime-addendums-use-credit-btn-click`,
        title: t('l.credit_usage.product.onetime_addendums.title', 'One-time Addendums'),
        icon: LeaseaAndPayments,
        useCreditText: t('l.credit_usage.product.use_credit.action', 'Use Credit'),
        useCreditOnClick: showModal('addendumsDialog'),
        useCreditVariant: 'ghost',
        credits: t('l.credit_usage.product.unlimited.title', 'Unlimited')
      }
    ],
    [t, showModal, planText]
  )

  const CUSTOM_ADDENDUM: TableItem[] = useMemo(
    () => [
      {
        title: t('l.credit_usage.product.custom_addendums.title', 'Custom Addendums'),
        icon: LeaseaAndPayments,
        useCreditText: t('l.credit_usage.product.use_credit.action', 'Use Credit'),
        useCreditOnClick: showModal('addendumsDialog'),
        useCreditVariant: 'ghost',
        useCreditId: `usage-credit-${planText}-custom-addendums-use-credit-btn-click`,
        credits: t('l.credit_usage.product.unlimited.title', 'Unlimited')
      }
    ],
    [t, showModal, planText]
  )

  const USERS: TableItem = useMemo(
    () => ({
      useCreditId: `usage-credit-${planText}-users-add-user-btn-click`,
      title: t('l.credit_usage.product.additional_users.title', 'Additional users'),
      icon: UsersIcon,
      useCreditVariant: 'ghost',
      credits: managedUsers?.value
        ? `${managedUsers?.value - managedUsers?.usage?.current_value} / ${managedUsers?.value}`
        : '',

      ...getAdditionalUsersDetails()
    }),
    [
      t,
      managedUsers?.usage?.current_value,
      managedUsers?.value,
      getAdditionalUsersDetails,
      planText
    ]
  )
  const COMPANY_PAGE = useMemo(() => {
    return isGrowthPlan ? GROWTH_COMPANY_PAGE : BUSINESS_COMPANY_PAGE
  }, [isGrowthPlan, GROWTH_COMPANY_PAGE, BUSINESS_COMPANY_PAGE])

  const CUSTOM_ADDENDUMS = useMemo(
    () => (isGrowthPlan ? [] : CUSTOM_ADDENDUM),
    [isGrowthPlan, CUSTOM_ADDENDUM]
  )
  const MONTHLY_ANALYTICS = useMemo(
    () => (isGrowthPlan ? [] : ANALYTICS),
    [isGrowthPlan, ANALYTICS]
  )

  const tableData: TableItem[] = useMemo(
    () => [
      TENANT_SCREENING,
      SYNDICATION,
      ...MONTHLY_ANALYTICS,
      ...COMPANY_PAGE,
      ...STANDARD_ADDENDUMS,
      ...CUSTOM_ADDENDUMS,
      USERS
    ],
    [
      COMPANY_PAGE,
      CUSTOM_ADDENDUMS,
      MONTHLY_ANALYTICS,
      STANDARD_ADDENDUMS,
      TENANT_SCREENING,
      SYNDICATION,
      USERS
    ]
  )

  return (
    <>
      <ResponsiveRender above="tablet">
        <Table columns={columnDefinition} data={tableData} />
      </ResponsiveRender>
      <ResponsiveRender below="desktop">
        <Stack align="center" space="xsmall">
          {tableData?.map(item => {
            return <MobileCard card={item} key={item.title} />
          })}
        </Stack>
      </ResponsiveRender>
      <DialogWrapper
        visible={visible}
        closeModal={closeModal}
        showModal={showModal}
        selectedListing={selectedListing}
        handleSyndicationSubmit={handleSyndicationSubmit}
      />
    </>
  )
}

export default PlanProducts
